import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import classnames from 'classnames';
import moment from 'moment';
import { getLocale, useIntl } from 'umi';
import { useDebounce } from 'ahooks';
import { Segment } from 'tea-component';
import Filter from '@/pages/intelligence/components/Filter';
import * as filterKeyMap from '@/pages/intelligence/components/Filter/FilterBase/_config';
import useGenreComponent from '@/pages/intelligence/hooks/useGenreComponent';
import GenreSelector from '@intelligence/components/GenreSelectMiddleware';
import TipContext from '@/pages/home/components/TipContextComponents/TipContext';
import { GENRE_SEGMENT_OPTIONS } from '../../const';
import { IFilterProps } from '../../typings';
import { getGenreProfileGenreList } from '@/pages/intelligence/services/genreProfile';
import { GENRE_MARKET_PROFILE_GUIDE_CLASSNAMES } from '@intelligence/components/UserGuide/GenreMarketProfile';
import { updateInputListRegionOptions } from '@intelligence/utils/filterUtils';
import DataSourceSelector from '@/pages/intelligence/components/TeaSelector/AnnieSource';
import {
  GENRE_SOURCES,
  GENRE_SOURCES_NEW,
} from '@intelligence/components/GenreSelect/constants';

const GENRE_SEGMENT_OPTIONS_NEW = GENRE_SEGMENT_OPTIONS.map((item) => ({
  ...item,
  className:
    item?.value === 'mainsub'
      ? GENRE_MARKET_PROFILE_GUIDE_CLASSNAMES.ELEMENT1_HIGHLIGHT4
      : '',
}));

export default function GenreProfileFilter(props: IFilterProps) {
  const {
    filter,
    regionOpts,
    getRadioOptionsTips,
    entityType,
    onFilterChange,
    isLoading,
    buriedPointParams,
  } = props;

  const { formatMessage } = useIntl();
  const locale = getLocale();
  const dataSourceInfoIdMap: { [key: string]: string } = {
    mobile: 'dbi_genre_profile_data_source_info_mobile',
    total: 'dbi_genre_profile_data_source_info',
  };
  const dataSourceInfoId =
    dataSourceInfoIdMap[entityType] ||
    'dbi_genre_profile_data_source_info_pc_console';
  const granularityTypeMap: { [key: string]: string } = {
    daily: 'date',
    weekly: 'week',
    monthly: 'month',
  };

  const { genreRef, genreSource, mainGenres, subGenres, handleGenresChange } =
    useGenreComponent({});

  const genreData = useMemo(() => {
    return { genreSource, mainGenres, subGenres };
  }, [genreSource, mainGenres, subGenres]);

  /**
   * 由于这里会导致品类筛选器组件内useEffect执行3次（genreSource、mainGenres、subGenres 三个依次触发useEffect），就会导致品类筛选器组件内判断失误
   * 所以这里加个防抖，把三次change 合并为一个change，品类筛选器组件内 只触发一次useEffect，并在这一次useEffect内一次性拿到最新的genreSource、mainGenres、subGenres
   */
  const genreDataDebounced = useDebounce(genreData, { wait: 600 });

  const defaultValue = {
    [filterKeyMap.RANGE_TIME]: {
      type: 'month',
      value: ['', ''],
    },
    [filterKeyMap.REGION]: {
      regions: [],
      markets: ['global'],
      type: 'market',
    },
  };

  const defaultInputList = {
    RANGE_TIME: {
      type: filterKeyMap.RANGE_TIME,
      key: filterKeyMap.RANGE_TIME,
      calendarType: 'month',
      showSelectType: true,
      showShortcut: false,
      default: [
        moment().subtract(6, 'month').startOf('month'),
        moment().subtract(1, 'month').endOf('month'),
      ],
    },
    REGION: {
      extendFilterType: filterKeyMap.REGION_EXTEND_TYPE.common,
      key: filterKeyMap.REGION,
      default: defaultValue[filterKeyMap.REGION],
      visible: 1,
      options: [],
      entityType,
    },
  };

  const formValueRef = useRef(JSON.parse(JSON.stringify(defaultValue)));

  const [inputList, setInputList] = useState([
    defaultInputList.REGION,
    defaultInputList.RANGE_TIME,
  ]);
  const genreAnnieDisabled = useMemo(() => {
    return entityType !== 'mobile';
  }, [entityType]);

  const handleFilterChange = (
    _value /** 筛选器数据 */,
    apiValue /** 筛选器转换后的数据 */,
  ) => {
    // if (isLoading) return;
    const { rangeTime, region } = apiValue || {};
    const { start_date, end_date, granularity } = rangeTime || {};
    const { regions, markets } = region;
    onFilterChange({
      type: 'cascadeFilter',
      payload: {
        start_date,
        end_date,
        granularity,
        regions,
        markets,
      },
    });
  };

  const onReset = useCallback((_value) => {
    // console.log(_value);
  }, []);

  const handleGenretorChange = (payload: any) => {
    const preGenreVal = {
      source: filter.genreSource,
      mainGenres: filter.mainGenres,
      subGenres: filter.subGenres,
    };

    if (JSON.stringify(preGenreVal) === JSON.stringify(payload)) {
      // 没有任何操作时不调用接口
      return;
    }
    const { source, mainGenres, subGenres } = payload;
    // if (isLoading) return;
    const mainGenresTmp = mainGenres || [];
    const subGenresTmp = subGenres || [];
    onFilterChange({
      type: 'cascadeGenres',
      payload: {
        source,
        mainGenres: mainGenresTmp,
        subGenres: subGenresTmp,
      },
    });
  };
  const entityTypeMap: { [key: string]: string[] } = {
    mobile: ['mobile'],
    pc: ['pc'],
    console: ['console'],
    combine: ['pc', 'console'],
  };
  // 手游和其他端获取品类的接口是不一样的
  const createFetchGenres = (): Promise<any> => {
    return getGenreProfileGenreList({
      need_filter: entityType === 'mobile',
      entity_type: entityTypeMap?.[entityType] || [],
    });
  };

  /** 时间筛选器值由父组件更新 */
  useEffect(() => {
    formValueRef.current = {
      region: {
        regions: filter.regions,
        markets: filter.markets,
        type: 'market',
      },
      rangeTime: {
        type: granularityTypeMap[filter.granularity] || 'month',
        value: [moment(filter.startDate), moment(filter.endDate)],
      },
    };
  }, [filter.startDate, filter.endDate, filter.granularity]);

  // useEffect(() => {
  //   const tempList = [...inputList];
  //   tempList[0].options = regionOpts;
  //   setInputList(tempList);
  // }, [regionOpts]);

  useEffect(() => {
    /** 国家筛选器的列表值 */
    const updateRegionOptionInput = updateInputListRegionOptions({
      inputList,
      regionOptions: !Array.isArray(regionOpts) ? [] : regionOpts,
      getRadioOptionsTips,
      regionKey: filterKeyMap.REGION,
    });
    setInputList(updateRegionOptionInput);
  }, [regionOpts, getRadioOptionsTips]);

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
      }}
      className="dbi-filter-genreProfile-container"
    >
      <div className="dbi-genre-filter">
        <Filter
          value={formValueRef.current}
          inputList={inputList}
          onConfirm={handleFilterChange}
          onReset={onReset}
          defaultValue={defaultValue}
          showButton={false}
        />
        <div className="search-item">
          <GenreSelector
            key="genreSelector"
            className={classnames(
              'stats-select',
              GENRE_MARKET_PROFILE_GUIDE_CLASSNAMES.ELEMENT1_HIGHLIGHT3,
            )}
            ref={genreRef}
            onConfirm={handleGenretorChange}
            disableAnnie={genreAnnieDisabled}
            sources={entityType === 'mobile' ? GENRE_SOURCES_NEW : GENRE_SOURCES}
            fetchAction={createFetchGenres}
            source={genreDataDebounced?.genreSource}
            mainGenres={genreDataDebounced?.mainGenres}
            subGenres={genreDataDebounced?.subGenres}
            onChange={handleGenresChange}
            entityType={entityType}
            buriedPointParams={buriedPointParams}
          />
        </div>
        <Segment
          disabled={isLoading}
          className="segment-light search-item"
          value={filter.genreType}
          onChange={(value: any) => {
            onFilterChange({ type: 'genreType', payload: value });
          }}
          options={GENRE_SEGMENT_OPTIONS_NEW?.map((t) => ({
            ...t,
            text: formatMessage({ id: t?.text }),
          }))}
        />
        {entityType === 'mobile' && (
          <DataSourceSelector
            type={'single'}
            key="mobile_single"
            locale={locale === 'en-US' ? 'en' : 'zh'}
            onConfirm={(val: any) => {
              onFilterChange({ type: 'sourceMetrics', payload: val });
            }}
            defaultValue={'sensortower'}
            value={filter.sourceMetrics}
            isShowButtonLabel={true}
          />
        )}
      </div>
      <div
        className="data-sesription"
        style={{
          width: '160px',
          // marginBottom: '24px',
          whiteSpace: 'nowrap',
          textAlign: 'right',
          position: 'relative',
          top: '8px',
          height: '32px',
        }}
      >
        {formatMessage({ id: 'dbi_genre_profile_data_desc' })}&nbsp;&nbsp;
        <TipContext
          className="genre-filter-tip"
          tipText={formatMessage({
            id: dataSourceInfoId,
          })}
        />
      </div>
    </div>
  );
}
